import { type ReactElement } from 'react'
import { isEmpty, isNotDefined, isNotEmpty, type WorkflowData } from '@terros/common'
import { Link, createFileRoute } from '@tanstack/react-router'
import { Group, TextInput, Stack, Title } from '@mantine/core'
import { useProfile, useTranslation, useWorkflowForCanvassFeature, useWorkflows } from '@front/model'
import { ButtonAdd, Page, NotReady, WorkflowSelect, ExportButton, SavedViews, SavedViewsError } from '@components'
import { useAccountDrawer } from '@hooks'
import { AccountModal } from '../areaManagement/accountDetail/AccountModal'
import { useAccountViews } from './views/useAccountViews'
import { useInfiniteServerState } from './useInfiniteServerState'
import { useClientState } from './useClientState'
import { ToggleAccountColumns } from './table/ToggleAccountColumns'
import { TanStackAccountsTable } from './table/TanStackAccountsTable'
import { useAccountGroupBy } from './table/groupBy/useAccountGroupBy'
import { GroupedAccountsView } from './table/groupBy/GroupedAccountsView'
import { GroupBySelect } from './table/groupBy/GroupBySelect'
import { AccountFilters } from './filters/AccountFilters'
import { AccountBulkActions } from './bulkActions/AccountBulkActions'
import { AccountCountText } from './AccountCountText'

function Accounts(): ReactElement {
  const accountDrawer = useAccountDrawer()
  const { hasPermission } = useProfile()
  const { t } = useTranslation()
  const clientState = useClientState()
  const viewsState = useAccountViews(clientState)
  const { isEnabled: isWorkflowEnabled, isLoading: isCheckingFeature } = useWorkflowForCanvassFeature()
  const {
    selectedColumns,
    query,
    setQuery,
    sorting,
    setSorting,
    selectedAccounts,
    setSelectedAccounts,
    clearSelection,
    setFilter,
    groupBy,
    setGroupBy,
  } = clientState
  const infiniteState = useInfiniteServerState(clientState)
  const { groups, isLoading: isGroupsLoading } = useAccountGroupBy({
    groupBy,
    filter: clientState.filter,
    range: clientState.range,
    workflow: clientState.workflow,
  })
  const isGrouped = groupBy !== 'none'
  const { refetch, data } = infiniteState
  const isFetching = isGrouped ? isGroupsLoading : infiniteState.isFetching
  const { data: workflows, isLoading: isWorkflowLoading } = useWorkflows()

  const renderContent = (): ReactElement => {
    if (isWorkflowEnabled && isNotDefined(clientState.workflow)) {
      if (isEmpty(workflows)) {
        return <Page.Hero icon='empty_dashboard' title='No workflows found' />
      }
      return (
        <Page.Hero
          icon='empty_dashboard'
          title='No workflow'
          description='You need to select a workflow to view accounts.'
        />
      )
    }
    if (isGrouped) {
      return (
        <GroupedAccountsView
          groupBy={groupBy}
          groups={groups}
          isLoading={isGroupsLoading}
          filter={clientState.filter}
          range={clientState.range}
          query={clientState.query}
          sorting={sorting}
          selectedColumns={selectedColumns}
          selectedAccounts={selectedAccounts}
          setSelectedAccounts={setSelectedAccounts}
          workflow={clientState.workflow}
          onRowClick={(row) => accountDrawer.open(row.accountId)}
        />
      )
    }
    return (
      <TanStackAccountsTable
        selectedColumns={selectedColumns}
        selectedAccounts={selectedAccounts}
        setSelectedAccounts={setSelectedAccounts}
        sorting={sorting}
        setSorting={setSorting}
        onRowClick={(row) => accountDrawer.open(row.accountId)}
        {...infiniteState}
      />
    )
  }

  const handleWorkflowSelected = (workflow: WorkflowData): void => {
    setFilter((prev) => ({
      ...prev,
      workflowId: workflow.workflowId,
      stageIds: workflow.stages.map((s) => s.stageId),
    }))
  }

  if (isCheckingFeature || isWorkflowLoading) return <NotReady isLoading />

  return (
    <Page>
      <Page.Heading
        isFetching={isFetching}
        refetch={refetch}
        metadata={{ title: t('Accounts') }}
        title={
          <>
            <Title order={2}>{t('Accounts')}</Title>
            <SavedViews state={viewsState} />
          </>
        }
      >
        {hasPermission('account:add') && <ButtonAdd component={Link} to='add' />}
      </Page.Heading>
      <Page.FunctionBar>
        <Stack w='100%' gap='sm'>
          <SavedViewsError state={viewsState} />
          <Group justify='space-between' gap='sm'>
            <Group gap='sm'>
              <WorkflowSelect onSelected={handleWorkflowSelected} />
              <TextInput placeholder={t('Filter')} value={query} onChange={(e) => setQuery(e.currentTarget.value)} />
              <AccountCountText loaded={data.length} total={infiniteState.total} isGrouped={isGrouped} />
            </Group>
            <Group gap='sm'>
              <GroupBySelect groupBy={groupBy} setGroupBy={setGroupBy} />
              <ToggleAccountColumns {...clientState} />
              <ExportButton tooltip={t('Export accounts')} />
            </Group>
          </Group>
          <AccountFilters clientState={clientState} />
        </Stack>
      </Page.FunctionBar>
      <Page.Content>{renderContent()}</Page.Content>
      {isNotEmpty(selectedAccounts) && (
        <AccountBulkActions
          selectedAccounts={selectedAccounts}
          isWorkflowEnabled={isWorkflowEnabled}
          workflow={clientState.workflow}
          clearSelection={clearSelection}
        />
      )}
      <AccountModal accountId={accountDrawer.state} onClose={accountDrawer.close} />
    </Page>
  )
}

export const Route = createFileRoute('/account/')({
  component: Accounts,
})
